import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as vehicleTypesApi from './vehicle-types.api';

import type {
  CreateVehicleTypeRequest,
  UpdateVehicleTypeRequest,
  VehicleTypeListQuery,
} from '@utazzo/shared';

const QK = {
  list: (query: VehicleTypeListQuery) => ['vehicle-types', 'list', query] as const,
  detail: (id: string) => ['vehicle-types', 'detail', id] as const,
};

export const useVehicleTypesList = (query: VehicleTypeListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => vehicleTypesApi.listVehicleTypes(query),
    staleTime: 30_000,
  });

export const useCreateVehicleType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateVehicleTypeRequest) => vehicleTypesApi.createVehicleType(payload),
    onSuccess: (vt) => {
      qc.invalidateQueries({ queryKey: ['vehicle-types', 'list'] });
      toast.success(`Added ${vt.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add vehicle type'),
  });
};

export const useUpdateVehicleType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateVehicleTypeRequest }) =>
      vehicleTypesApi.updateVehicleType(id, payload),
    onSuccess: (vt) => {
      qc.invalidateQueries({ queryKey: ['vehicle-types', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(vt.id) });
      toast.success('Vehicle type updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update vehicle type'),
  });
};

export const useDeleteVehicleType = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => vehicleTypesApi.deleteVehicleType(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vehicle-types', 'list'] });
      toast.success('Vehicle type deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete vehicle type'),
  });
};

export const useBulkDeleteVehicleTypes = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => vehicleTypesApi.bulkDeleteVehicleTypes(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicle-types'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete vehicle types'),
  });
};

export const useBulkSetVehicleTypesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      vehicleTypesApi.bulkSetVehicleTypesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicle-types'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update vehicle type status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
